import api from '@/lib/api';

/**
 * Telefonie (Telnyx WebRTC).
 *
 * Der Audiostrom läuft direkt zwischen Browser und Telnyx — nicht über unser
 * Backend. Diese Endpunkte liefern nur das kurzlebige Login-Token für das SDK
 * sowie die Anrufhistorie.
 */

export interface VoiceConfig {
  organization_id?: string;
  /** Immer maskiert ("[HIDDEN]") oder null — der Klartext verlässt den Server nie. */
  api_key: string | null;
  /** Wird beim ersten Nummernkauf automatisch angelegt. */
  connection_id: string | null;
  /** Die eigene VynDesk-Rufnummer. */
  caller_id: string | null;
  phone_number_id?: string | null;
  /** Vorhandene Nummer beim bisherigen Anbieter, die hierher umgeleitet wird. */
  external_number?: string | null;
  is_active: boolean;
  /** Alle EINGEHENDEN Anrufe automatisch aufzeichnen (Ansage-Pflicht beachten!). */
  record_all?: boolean;
  last_test_at?: string | null;
  last_test_status?: string | null;
  last_test_error?: string | null;
}

export interface OrgNumber {
  id: string;
  phone_number: string;
  status: string;
  is_primary: boolean;
}

export interface AvailableNumber {
  phone_number: string;
  number_type: string;
  region: string | null;
  monthly_cost: string | null;
  upfront_cost: string | null;
  currency: string | null;
  /** Telnyx verdeckt Ziffern, solange das Konto nicht verifiziert ist. */
  masked: boolean;
  requirements: string[];
}

export interface VoiceSettings {
  /** API-Key und Connection-ID hinterlegt. */
  connected: boolean;
  /** Zusätzlich aktiviert und Caller-ID gesetzt — erst dann kann gewählt werden. */
  callingEnabled: boolean;
  config: VoiceConfig;
}

export interface VoiceToken {
  /** SIP-Login der Org-Connection — die einzige Identität, die auch Anrufe EMPFANGEN kann. */
  login: string;
  password: string;
  callerId: string | null;
}

export interface TelnyxPhoneNumber {
  id: string;
  phone_number: string;
  status: string;
  connection_id: string | null;
}

export type CallDirection = 'outbound' | 'inbound';
export type CallStatus =
  | 'initiated' | 'ringing' | 'active' | 'queued'
  | 'completed' | 'failed' | 'no_answer' | 'busy' | 'cancelled';

export interface QueueState {
  count: number;
  items: {
    id: string;
    from_number: string | null;
    started_at: string;
    organization_id: string;
    organization_name: string;
  }[];
}

export interface CallRecord {
  id: string;
  organization_id: string;
  direction: CallDirection;
  from_number: string | null;
  to_number: string | null;
  status: CallStatus;
  user_id: string | null;
  ticket_id: string | null;
  customer_email: string | null;
  telnyx_call_id: string | null;
  telnyx_leg_id?: string | null;
  /** Nur ein Marker „Aufzeichnung vorhanden" — der Link selbst verfällt nach 10 min. */
  recording_url?: string | null;
  transcript?: string | null;
  summary?: string | null;
  summarized_at?: string | null;
  /** Wer hat das Gespräch geführt: 'agent' | 'ai'. */
  handled_by?: string | null;
  hangup_cause: string | null;
  notes: string | null;
  started_at: string;
  answered_at: string | null;
  ended_at: string | null;
  duration_seconds: number;
  first_name?: string | null;
  last_name?: string | null;
  user_email?: string | null;
  ticket_subject?: string | null;
}

export interface CallPage {
  items: CallRecord[];
  total: number;
  hasMore: boolean;
}

export const CALLS_PAGE_SIZE = 25;

// ---- Leitungen (Mehr-Organisationen-Betrieb) ----

/** Eine aktive Rufnummer einer Organisation, in der der Nutzer Mitglied ist. */
export interface VoiceLine {
  organization_id: string;
  organization_name: string;
  number: string;
  /** SIP-Login der Org-Connection — Browser registriert JEDE Leitung parallel. */
  login: string;
  password: string;
}

/** `organizationId` als Query-Param — undefined fällt serverseitig auf die Primär-Org zurück. */
const orgParams = (organizationId?: string) => (organizationId ? { organizationId } : {});

// ---- Regulatorische Nachweise (z. B. deutsche Nummern) ----

export interface NumberRequirement {
  requirement_id: string;
  field_type: 'textual' | 'document' | 'address';
  field_value: string | null;
  name: string;
  description: string | null;
  example: string | null;
}

export interface PendingRequirementOrder {
  order_id: string;
  sub_number_order_id: string;
  phone_numbers: string[];
  country_code: string;
  status: string;
  requirements: NumberRequirement[];
}


export type AiMode = 'off' | 'fallback' | 'always';

export interface AssistantInput {
  ai_mode: AiMode;
  name: string;
  /** 'ai_first' = KI begrüßt; 'caller_first' = KI wartet, bis der Anrufer spricht. */
  greeting_mode: 'ai_first' | 'caller_first';
  greeting: string;
  instructions: string;
  language: string;
  tools: { orders: boolean; tickets: boolean; transfer: boolean };
}

export interface AssistantState {
  configured: boolean;
  ai_mode: AiMode;
  config: AssistantInput | null;
}

const voiceService = {
  async getSettings(organizationId?: string): Promise<VoiceSettings> {
    const { data } = await api.get('/voice/settings', { params: orgParams(organizationId) });
    return data.data;
  },

  async saveSettings(payload: Partial<VoiceConfig>, organizationId?: string): Promise<VoiceSettings> {
    const { data } = await api.put('/voice/settings', { ...payload, ...orgParams(organizationId) });
    return data.data;
  },

  async testSettings(organizationId?: string): Promise<{ success: boolean; error: string | null; connectionName?: string }> {
    const { data } = await api.post('/voice/settings/test', orgParams(organizationId));
    return data.data;
  },

  async listPhoneNumbers(organizationId?: string): Promise<TelnyxPhoneNumber[]> {
    const { data } = await api.get('/voice/phone-numbers', { params: orgParams(organizationId) });
    return data.data;
  },

  /** Der Organisation zugewiesene Rufnummern. */
  async listNumbers(organizationId?: string): Promise<OrgNumber[]> {
    const { data } = await api.get('/voice/numbers', { params: orgParams(organizationId) });
    return data.data;
  },

  async searchNumbers(params: { country?: string; locality?: string; type?: string; limit?: number; organizationId?: string }): Promise<AvailableNumber[]> {
    const { data } = await api.get('/voice/numbers/available', { params });
    return data.data;
  },

  async orderNumber(phone_number: string, organizationId?: string): Promise<{ phone_number: string; orderId: string | null; status: string }> {
    const { data } = await api.post('/voice/numbers/order', { phone_number, ...orgParams(organizationId) });
    return data.data;
  },

  /** Bestellungen laufen asynchron — hiermit den aktuellen Stand nachladen. */
  async refreshNumbers(organizationId?: string): Promise<{ active: boolean; numbers: OrgNumber[] }> {
    const { data } = await api.post('/voice/numbers/refresh', orgParams(organizationId));
    return data.data;
  },

  /** Alle aktiven Leitungen der eigenen Organisationen — eine je Org mit Nummer. */
  async getLines(): Promise<VoiceLine[]> {
    const { data } = await api.get('/voice/lines');
    return data.data;
  },

  /** Bereits gekaufte Nummer einer anderen (eigenen) Organisation zuweisen. */
  async assignNumber(phone_number: string, target_organization_id: string): Promise<void> {
    await api.post('/voice/numbers/assign', { phone_number, target_organization_id });
  },

  /** Warteschlange: wartende Anrufer. */
  async getQueue(): Promise<QueueState> {
    const { data } = await api.get('/voice/queue');
    return data.data;
  },

  /** Nächsten Wartenden annehmen — er klingelt dann im Browser. */
  async pullNextFromQueue(organizationId?: string): Promise<void> {
    await api.post('/voice/queue/next', orgParams(organizationId));
  },

  /** KI-Assistent: aktuelle Konfiguration. */
  async getAssistant(organizationId?: string): Promise<AssistantState> {
    const { data } = await api.get('/voice/assistant', { params: orgParams(organizationId) });
    return data.data;
  },

  /** KI-Assistent anlegen/aktualisieren — das LLM stellt der Server, unsichtbar für den Kunden. */
  async saveAssistant(input: AssistantInput, organizationId?: string): Promise<{ assistantId: string; ai_mode: AiMode }> {
    const { data } = await api.post('/voice/assistant', { ...input, ...orgParams(organizationId) });
    return data.data;
  },

  async setAiMode(ai_mode: AiMode, organizationId?: string): Promise<void> {
    await api.post('/voice/assistant/mode', { ai_mode, ...orgParams(organizationId) });
  },

  /** Laufendes KI-Gespräch übernehmen: KI stoppt, Anruf klingelt im Browser. */
  async takeoverCall(callId: string): Promise<void> {
    await api.post(`/voice/calls/${callId}/takeover`);
  },

  /** Whisper-Transkript + KI-Zusammenfassung — einmal berechnet, dann gespeichert. */
  async summarizeCall(callId: string, force = false): Promise<{ transcript: string; summary: string; cached: boolean }> {
    const { data } = await api.post(`/voice/calls/${callId}/summary`, { force });
    return data.data;
  },

  /** Frischen Wiedergabe-Link holen — der gespeicherte verfällt nach 10 Minuten. */
  async getRecordingUrl(callId: string): Promise<string> {
    const { data } = await api.get(`/voice/calls/${callId}/recording`);
    return data.data.url;
  },

  /** Aufzeichnung des laufenden EINGEHENDEN Anrufs starten/stoppen. */
  async startRecording(): Promise<void> {
    await api.post('/voice/record/start');
  },

  async stopRecording(): Promise<void> {
    await api.post('/voice/record/stop');
  },

  async setExternalNumber(external_number: string | null, organizationId?: string): Promise<{ external_number: string | null }> {
    const { data } = await api.put('/voice/external-number', { external_number, ...orgParams(organizationId) });
    return data.data;
  },


  /** Offene regulatorische Nachweise für Nummernbestellungen. */
  async listRequirements(organizationId?: string): Promise<PendingRequirementOrder[]> {
    const { data } = await api.get('/voice/requirements', { params: orgParams(organizationId) });
    return data.data;
  },

  async uploadRequirementDocument(file: File, organizationId?: string): Promise<{ documentId: string; filename: string }> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await api.post('/voice/requirements/document', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: orgParams(organizationId),
    });
    return data.data;
  },

  async createRequirementAddress(fields: {
    first_name?: string; last_name?: string; business_name?: string;
    street_address: string; locality: string; postal_code?: string; country_code?: string;
  }, organizationId?: string): Promise<{ addressId: string }> {
    const { data } = await api.post('/voice/requirements/address', { ...fields, ...orgParams(organizationId) });
    return data.data;
  },

  async submitRequirements(sub_number_order_id: string, values: { requirement_id: string; field_value: string }[], organizationId?: string) {
    const { data } = await api.post('/voice/requirements/submit', { sub_number_order_id, values, ...orgParams(organizationId) });
    return data.data as { requirements_met: boolean; status: string };
  },

  /** Für Agenten: ist Telefonie eingerichtet und aktiv? Enthält keine Geheimnisse. */
  async getStatus(): Promise<{ callingEnabled: boolean; callerId: string | null }> {
    const { data } = await api.get('/voice/status');
    return data.data;
  },

  async getToken(): Promise<VoiceToken> {
    const { data } = await api.post('/voice/token');
    return data.data;
  },

  async listCalls(params: { limit?: number; offset?: number; ticketId?: string; direction?: CallDirection } = {}): Promise<CallPage> {
    const { data } = await api.get('/voice/calls', { params });
    return data.data;
  },

  async startCall(payload: {
    direction?: CallDirection;
    from_number?: string | null;
    to_number?: string | null;
    ticket_id?: string | null;
    customer_email?: string | null;
    telnyx_call_id?: string | null;
  }): Promise<CallRecord> {
    const { data } = await api.post('/voice/calls', payload);
    return data.data;
  },

  async updateCall(id: string, payload: {
    status?: CallStatus;
    telnyx_call_id?: string;
    hangup_cause?: string;
    notes?: string;
  }): Promise<CallRecord> {
    const { data } = await api.patch(`/voice/calls/${id}`, payload);
    return data.data;
  },
};

export default voiceService;
